import React, { useState } from "react";
import { Button, Tag, Modal, Input, message } from "antd";
import { GlobalOutlined, StopOutlined } from "@ant-design/icons";
import { updateGravePublicStatus } from "./publicContentApi";
import { useActorContext } from "../../../auth/ActorContext";
import { can } from "../../../auth/effectivePermissions";
import type { CurrentUser } from "../../../api/currentUser";

export interface GravePublicStatusControlProps {
  cemeteryId: string;
  graveId: string;
  currentState?: "PUBLISHED" | "WITHDRAWN";
  assetStale?: boolean;
  onStatusChanged?: (newState: "PUBLISHED" | "WITHDRAWN") => void;
}

export function GravePublicStatusControl({
  cemeteryId,
  graveId,
  currentState = "WITHDRAWN",
  assetStale = false,
  onStatusChanged,
}: GravePublicStatusControlProps): React.JSX.Element | null {
  let actor: CurrentUser | null = null;
  try {
    const actorState = useActorContext();
    actor =
      actorState.status === "ready" || actorState.status === "refreshing" || actorState.status === "stale"
        ? actorState.actor
        : null;
  } catch {
    actor = null;
  }

  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [pendingAction, setPendingAction] = useState<"PUBLISHED" | "WITHDRAWN">("PUBLISHED");
  const [reason, setReason] = useState("");

  const canPublish =
    actor !== null
    && (can(actor, "PUBLIC_PUBLISH", { type: "CEMETERY", id: cemeteryId })
      || can(actor, "PUBLIC_PUBLISH", { type: "GRAVE", id: graveId }));

  if (!canPublish) {
    return null;
  }

  const isPublished = currentState === "PUBLISHED";

  const handleOpenConfirm = (action: "PUBLISHED" | "WITHDRAWN") => {
    setPendingAction(action);
    setReason("");
    setModalVisible(true);
  };

  const handleConfirm = () => {
    setLoading(true);
    void updateGravePublicStatus(cemeteryId, graveId, pendingAction, reason.trim() || undefined)
      .then(() => {
        void message.success(
          pendingAction === "PUBLISHED" ? "Đã công khai mộ thành công" : "Đã thu hồi mộ khỏi scene công khai"
        );
        setModalVisible(false);
        onStatusChanged?.(pendingAction);
      })
      .catch((err: unknown) => {
        void message.error((err as Error).message || "Không thể cập nhật trạng thái công khai");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 12, color: "var(--ncc-color-text-secondary)" }}>Trạng thái công khai:</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Tag color={isPublished ? "green" : "default"}>
            {isPublished ? "Đã công khai" : "Chưa công khai"}
          </Tag>
          {assetStale && isPublished && (
            <Tag color="orange" title="Quyết định mới chưa được phát hành thành asset 3D">
              Chờ phát hành
            </Tag>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        {isPublished ? (
          <Button
            danger
            size="small"
            icon={<StopOutlined />}
            loading={loading}
            onClick={() => {
              handleOpenConfirm("WITHDRAWN");
            }}
            style={{ flex: 1 }}
          >
            Thu hồi
          </Button>
        ) : (
          <Button
            type="primary"
            size="small"
            icon={<GlobalOutlined />}
            loading={loading}
            onClick={() => {
              handleOpenConfirm("PUBLISHED");
            }}
            style={{ flex: 1, backgroundColor: "var(--ncc-color-action-primary)" }}
          >
            Công khai
          </Button>
        )}
      </div>

      <Modal
        title={pendingAction === "PUBLISHED" ? "Xác nhận công khai mộ" : "Xác nhận thu hồi mộ"}
        open={modalVisible}
        onOk={handleConfirm}
        onCancel={() => {
          setModalVisible(false);
        }}
        confirmLoading={loading}
        okText="Xác nhận"
        cancelText="Huỷ"
      >
        <p>
          {pendingAction === "PUBLISHED"
            ? "Mộ sẽ được đưa vào danh sách hiển thị trên scene 3D và cổng tra cứu thân nhân."
            : "Mộ sẽ biến mất khỏi scene 3D công khai ngay sau khi phát hành lại asset."}
        </p>
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>
            Lý do (tuỳ chọn):
          </label>
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            placeholder="Nhập lý do thực hiện thao tác..."
            maxLength={500}
          />
        </div>
      </Modal>
    </div>
  );
}
